// frontend/src/quiz_management/quiztaker.js
// REVISED: Complete version with all original functionality

import React, { useState, useEffect, useRef } from 'react';
import './quiztaker.css';
import API_BASE_URL from './config';
import { API_BASE, authFetch } from '../services/api.js';


const QuizTaker = ({ quizId, studentId, onQuizComplete }) => {
    const [quiz, setQuiz] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [showResults, setShowResults] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(null);
    const [questionTimes, setQuestionTimes] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    
    // NEW: Store start time and quiz results in frontend only
    const [quizStartTime, setQuizStartTime] = useState(null);
    const [quizResults, setQuizResults] = useState(null);
    
    const timerRef = useRef(null);
    const questionStartTimes = useRef({});

    // Add this function to track current question based on scroll position
    const updateCurrentQuestion = () => {
        const questions = document.querySelectorAll('.question-container');
        const scrollPosition = window.scrollY + window.innerHeight / 2;
        
        let currentIndex = 0;
        questions.forEach((q, index) => {
            const qTop = q.offsetTop;
            const qBottom = qTop + q.offsetHeight;
            
            if (scrollPosition >= qTop && scrollPosition < qBottom) {
                currentIndex = index;
            }
        });
        
        if (currentIndex !== currentQuestionIndex) {
            setCurrentQuestionIndex(currentIndex);
            scrollNavToQuestion(currentIndex);
        }
    };

    // Add this function to auto-scroll navigation
    const scrollNavToQuestion = (questionIndex) => {
        const navContainer = document.querySelector('.nav-buttons');
        const navBtn = document.querySelector(`.nav-btn[data-question="${questionIndex}"]`);
        
        if (navContainer && navBtn) {
            const containerWidth = navContainer.offsetWidth;
            const btnLeft = navBtn.offsetLeft;
            const btnWidth = navBtn.offsetWidth;
            
            const scrollLeft = btnLeft - (containerWidth / 2) + (btnWidth / 2);
            navContainer.scrollTo({
                left: scrollLeft,
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        loadQuizData();
        
        // Scroll to top when quiz loads
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
        
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [quizId]);

    useEffect(() => {
        const handleScroll = () => {
            updateCurrentQuestion(); // Add this line
            
            const questions = document.querySelectorAll('.question-container');
            const scrollPosition = window.scrollY + window.innerHeight / 2;
            
            questions.forEach((q, index) => {
                const qTop = q.offsetTop;
                const qBottom = qTop + q.offsetHeight;
                
                if (scrollPosition > qTop && scrollPosition < qBottom) {
                    trackQuestionTime(index);
                }
            });
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [currentQuestionIndex]); // Add currentQuestionIndex as dependency

    const loadQuizData = async () => {
        try {
            setLoading(true);
            
            // Load quiz details
            const quizResponse = await authFetch(`${API_BASE_URL}/quiz/quiz/${quizId}?student_id=${studentId}`);
            const quizData = await quizResponse.json();
            setQuiz(quizData);
            
            // Load quiz questions
            const questionsResponse = await authFetch(`${API_BASE_URL}/quiz/quiz/${quizId}/questions?student_id=${studentId}`);
            const questionsData = await questionsResponse.json();
            setQuestions(questionsData);
            
            // REVISED: Record start time in frontend state only (no database record)
            setQuizStartTime(new Date());
            
            // Initialize timer if quiz has time limit
            if (quizData.time_limit_minutes) {
                setTimeRemaining(quizData.time_limit_minutes * 60);
                startTimer();
            }
            
            // Initialize question timing
            questionsData.forEach((_, index) => {
                questionStartTimes.current[index] = Date.now();
            });
            
        } catch (err) {
            setError('Failed to load quiz. Please try again.');
            console.error('Quiz loading error:', err);
        } finally {
            setLoading(false);
        }
    };

    const startTimer = () => {
        timerRef.current = setInterval(() => {
            setTimeRemaining(prev => {
                if (prev <= 1) {
                    handleAutoSubmit();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const trackQuestionTime = (questionIndex) => {
        const now = Date.now();
        const startTime = questionStartTimes.current[questionIndex];
        
        if (startTime) {
            const timeSpent = Math.floor((now - startTime) / 1000);
            setQuestionTimes(prev => ({
                ...prev,
                [questionIndex]: (prev[questionIndex] || 0) + timeSpent
            }));
        }
        
        questionStartTimes.current[questionIndex] = now;
    };

    // REVISED: Only store answers in frontend state (no backend calls)
    const selectOption = async (questionIndex, option) => {
        // Record start time on first answer selection if not already recorded
        if (!quizStartTime) {
            setQuizStartTime(new Date());
        }
        
        setSelectedAnswers(prev => ({
            ...prev,
            [questionIndex]: option
        }));

        // Track time for this question
        trackQuestionTime(questionIndex);
        updateNavigation();
    };

    const updateNavigation = () => {
        const navBtns = document.querySelectorAll('.nav-btn');
        navBtns.forEach((btn, index) => {
            const answered = selectedAnswers[index];
            btn.classList.toggle('answered', !!answered);
            
            // Update current question highlighting
            const questions = document.querySelectorAll('.question-container');
            const scrollPosition = window.scrollY + window.innerHeight / 2;
            
            questions.forEach((q, qIndex) => {
                const qTop = q.offsetTop;
                const qBottom = qTop + q.offsetHeight;
                
                if (scrollPosition >= qTop && scrollPosition < qBottom) {
                    btn.classList.add('current');
                } else {
                    btn.classList.remove('current');
                }
            });
        });
    };

    // REVISED: Submit everything at once - create attempt + save answers + calculate results
    const submitQuiz = async () => {
        if (isSubmitting) return;
        
        setIsSubmitting(true);
        
        try {
            const endTime = new Date();
            
            // Prepare submission data
            const submissionData = {
                quiz_id: quizId,
                student_id: studentId,
                start_time: quizStartTime.toISOString(),
                end_time: endTime.toISOString(),
                answers: Object.keys(selectedAnswers).map(questionIndex => ({
                    quiz_question_id: questions[questionIndex].quiz_question_id,
                    selected_answer: selectedAnswers[questionIndex],
                    time_taken_seconds: questionTimes[questionIndex] || 0
                }))
            };
            
            // Submit everything at once
            const response = await authFetch(`${API_BASE_URL}/quiz/attempt/submit-complete?student_id=${studentId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(submissionData)
            });
            
            if (response.ok) {
                const results = await response.json();
                setQuizResults(results);
                setShowResults(true);
                
                if (timerRef.current) {
                    clearInterval(timerRef.current);
                }
                
                // Don't call onQuizComplete to keep results on page
                // if (onQuizComplete) {
                //     onQuizComplete(results);
                // }
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to submit quiz');
            }
        } catch (err) {
            console.error('Error submitting quiz:', err);
            setError('Failed to submit quiz. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAutoSubmit = () => {
        if (!showResults) {
            submitQuiz();
        }
    };

    const scrollToQuestion = (index) => {
        const question = document.querySelector(`.question-container[data-question="${index}"]`);
        if (question) {
            const navHeight = document.querySelector('.nav-container')?.offsetHeight || 0;
            const questionHeading = question.querySelector('h3')?.offsetHeight || 0;
            const yOffset = -(navHeight + questionHeading + 15);
            const y = question.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const reattemptQuiz = () => {
        window.location.reload();
    };

    if (loading) {
        return (
            <div className="quiz-container">
                <div className="loading-spinner">Loading quiz...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="quiz-container">
                <div className="error-message">{error}</div>
            </div>
        );
    }

    return (
        <div className="quiz-container">
            <div className="quiz-header">
                <h1>{quiz?.quiz_title || 'MCQ Quiz'}</h1>
                <div className="quiz-info">
                    <span>Quiz Practice</span>
                    {timeRemaining !== null && (
                        <span className={`timer ${timeRemaining < 300 ? 'warning' : ''}`}>
                            {formatTime(timeRemaining)}
                        </span>
                    )}
                </div>
            </div>

            {/* Question Navigation */}
            <div className="nav-container">
                <div className="question-nav nav-buttons">
                    {questions.map((_, index) => (
                        <button
                            key={index}
                            className={`nav-btn ${selectedAnswers[index] ? 'answered' : ''} ${index === currentQuestionIndex ? 'current' : ''}`}
                            data-question={index}
                            onClick={() => scrollToQuestion(index)}
                        >
                            {index + 1}
                        </button>
                    ))}
                </div>
            </div>

            {/* Questions */}
            <div className="questions-container">
                {questions.map((question, index) => (
                    <div key={question.quiz_question_id} className="question-container" data-question={index}>
                        <h3>Question {index + 1}</h3>
                        <p className="question-text">{question.question_text}</p>
                        
                        <div className="options-container">
                            {['A', 'B', 'C', 'D'].map(option => {
                                const optionText = question[`option${option}`];
                                if (!optionText) return null;
                                
                                return (
                                    <button
                                        key={option}
                                        className={`option-btn ${selectedAnswers[index] === option ? 'selected' : ''}`}
                                        onClick={() => selectOption(index, option)}
                                        disabled={showResults}
                                    >
                                        <span className="option-letter">{option}.</span>
                                        <span className="option-text">{optionText}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Show results after submission */}
                        {showResults && (
                            <div className="results-section">
                                <div className={`status ${selectedAnswers[index] === question.correct_answer ? 'correct' : 'incorrect'}`}>
                                    {selectedAnswers[index] === question.correct_answer ? 
                                        'Correct!' : 
                                        selectedAnswers[index] ? 'Incorrect' : 'No answer selected'
                                    }
                                </div>
                                
                                {selectedAnswers[index] !== question.correct_answer && (
                                    <div className="correct-answer-text">
                                        Correct Answer: {question[`option${question.correct_answer}`]}
                                    </div>
                                )}
                                
                                {question.explanation && (
                                    <div className="explanation">
                                        <strong>Explanation:</strong> {question.explanation}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Submit Button and Results */}
            <div className="submit-container">
                {!showResults ? (
                    <button 
                        className="submit-btn" 
                        onClick={submitQuiz}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Submitting...' : 'Submit Quiz'}
                    </button>
                ) : (
                    <>
                        <button className="submit-btn reattempt" onClick={reattemptQuiz}>
                            Reattempt Quiz
                        </button>
                        {quizResults && (
                            <div className="final-score" id="final-score">
                                Final Score: {quizResults.total_score}/{quizResults.total_questions} ({quizResults.percentage_score.toFixed(1)}%)
                                <br />
                                Time taken: {quizResults.time_taken_minutes} minute(s)
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default QuizTaker;